import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent4 as accent, useSlide4, Seg } from "./slide4.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 4 — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide4.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.65, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[]) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: "#ffffff", fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide4Mobile: React.FC = () => {
  const { meta: slide4Meta, facts: slide4Facts, perspective: slide4Perspective } = useSlide4();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#a06078", textTransform: "uppercase", marginBottom: 8 }}>
        {slide4Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide4Meta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 27, lineHeight: 1.18, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide4Meta.titlePlain} <span style={{ color: accent }}>{slide4Meta.titleAccent}</span>
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#a889a0", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide4Meta.subtitle}
      </p>

      {/* facts */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide4Meta.whatLabel}</div>
        {slide4Facts.map((f, i) => (
          <div key={i} style={{
            display: "flex", gap: 13, alignItems: "flex-start",
            background: "rgba(212,83,126,0.04)", border: "1px solid rgba(212,83,126,0.12)",
            borderRadius: 9, padding: "12px 14px", marginBottom: 9,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "rgba(212,83,126,0.4)", fontFamily: mono, flexShrink: 0, lineHeight: 1.2 }}>{f.num}</div>
            <div>
              <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 15, color: "#f0d8e0", marginBottom: 4 }}>{f.title}</div>
              <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 13.5, lineHeight: 1.55, color: "#9bbdd6", margin: 0 }}>{f.body}</p>
            </div>
          </div>
        ))}
      </div>

      {/* perspective callout */}
      <div style={{ background: "rgba(212,83,126,0.06)", border: "1px solid rgba(212,83,126,0.20)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide4Meta.perspectiveLabel}</div>
        <p style={{ ...txt, color: "#f0c8d8" }}>{renderSegs(slide4Perspective)}</p>
      </div>
    </div>
  );
};

export default HTLDSlide4Mobile;
